import { useEffect, useState } from "react";
import {
	DialogContent,
	Button,
	Typography,
	Box,
	Grid,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	Stack,
	Chip,
	TextField,
	Tooltip,
	Autocomplete,
	useTheme,
	IconButton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faHashtag,
	faCalendarAlt,
	faUser,
	faPerson,
	faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { createFilterOptions, isValidId, Util } from "../../util/Util";
import { AddCircleOutline, RemoveCircleOutline, ReplayOutlined } from "@mui/icons-material";
import ConfirmDialog from "../../components/ConfirmDialog";
import QuantityStepperInput from "../../components/QuantityStepperInput";
import { qtyMin, roundQty, addMoney, lineTotal } from "../../util/quantity";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import RefreshButton from "../../components/RefreshButton";
import { MYSQL_LIMITS } from "../../util/constants";

import { useAppSocket } from "../../providers/AppSocketContext";

interface ReturnItem {
	item: number;
	item_name: string;
	item_code: string;
	quantity: number;
	maxQuantity: number;
	amount: number;
	total: number;
	isFractional?: boolean;
}

interface ReturnedRecord {
	item: number;
	item_name: string;
	item_code: string;
	quantity: number;
}


interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle: any;
	onSuccess?: () => void;
}

const ReturnSaleModal = ({ isOpen, toggle, bundle, onSuccess }: Props) => {
	const [record, setRecord] = useState<any>(null);
	const [returnsRecord, setReturnsRecord] = useState<any>(null);
	const [returnItems, setReturnItems] = useState<ReturnItem[]>([]);
	const [returnedItems, setReturnedItems] = useState<ReturnedRecord[]>([]);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [returnDate, setReturnDate] = useState<Date | null>(null);
	const [description, setDescription] = useState("");
	const [errorInput, setErrorInput] = useState("");

	const { api } = useAuth();
	const { appSettings } = useAppSocket();
	const theme = useTheme();
	const warningColor = theme.palette.warning.main;
	const { setLoadingMessage, queueReRun, t } = useUI();

	const canValidate = returnItems.length > 0 && returnDate !== null;

	const fetchData = async () => {
		const res = await api.get(`sales/${bundle.batch}`);
		if (res) {
			if (res.status === 200) {
				setRecord(res.data);
				// Set default receiver to original client and date to today
				setReturnDate(new Date());
				setDescription("");
				return;
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else if (res.status === 404) {
				toast.warn(t("common:toast.warn.noRecord"));
			} else {
				console.error("Failed to fetch data:", res);
			}
		}
		toggle();
	};


	const fetchReturns = async () => {
		const res = await api.get(`returns/${bundle.batch}`);
		if (res) {
			if (res.status === 200) {
				setReturnsRecord(res.data);
				return;
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchReturns);
			} else if (res.status === 404) {
				toast.warn(t("common:toast.warn.noRecord"));
			} else {
				console.error("Failed to fetch data:", res);
			}
		}
		toggle();
	};



	useEffect(() => {
		if (isOpen && bundle?.batch) {
			fetchData();
			setReturnItems([]);
		}
	}, [isOpen, bundle?.batch]);

	useEffect(() => {
		if (record) {
			fetchReturns();
		}
	}, [record]);


	useEffect(() => {
		if (returnsRecord) {
			const mergedReturned: ReturnedRecord[] = [];
			returnsRecord.records.forEach((rec: any) => {
				const existing = mergedReturned.find((r) => r.item === rec.item_id);
				if (existing) {
					existing.quantity = roundQty(existing.quantity + rec.quantity);
				} else {
					mergedReturned.push({
						item: rec.item_id,
						item_name: rec.item_name,
						item_code: rec.item_code,
						quantity: rec.quantity,
					});
				}
			});
			setReturnedItems(mergedReturned);
		}
	}, [returnsRecord]);

	const isItemInReturnList = (item: any) => {
		return returnItems.some((ri) => ri.item === item.item_id && ri.amount === (item.unit_price || item.amount));
	};

	const handleAddItem = (item: any) => {
		if (isItemInReturnList(item)) return;
		const returnedItem = returnedItems.find((ret) => ret.item === item.item_id);
		setReturnItems((prev) => [
			...prev,
			{
				item: item.item_id,
				item_name: item.item_name,
				item_code: item.item_code,
				quantity: roundQty(item.quantity - (returnedItem?.quantity ?? 0)),
				maxQuantity: returnedItem?.quantity
					? roundQty(item.quantity - returnedItem.quantity)
					: item.quantity,
				amount: item.unit_price || item.amount,
				total: item.total,
				isFractional: item.is_fractional === true || item.IsFractional === true,
			},
		]);
	};

	const handleRemoveItem = (item: ReturnItem) => {
		setReturnItems((prev) => prev.filter((ri) => !(ri.item === item.item && ri.amount === item.amount)));
	};

	const handleQuantityChange = (item: ReturnItem, newQty: number) => {
		const min = qtyMin(!!item.isFractional);
		const clamped = roundQty(Math.max(min, Math.min(newQty, item.maxQuantity)));
		setReturnItems((prev) =>
			prev.map((ri) =>
				ri.item === item.item && ri.amount === item.amount
					? { ...ri, quantity: clamped, total: lineTotal(clamped, ri.amount) }
					: ri
			)
		);
	};

	const handleConfirm = async () => {
		setLoadingMessage("SAVE");

		const details = {
			batch: record.details.batch,
			...(Number(record.details.agent_id) > 1 && { agent: record.details.agent_id }),
			receiver: record.details.client_id,
			rec_date: Util.formatISODate(returnDate || new Date()),
			ref: record.details.reference,
			description: description || `Return for ${record.details.reference}`,
			source_depot: record.details.dest_id,
			dest_depot: record.details.src_id,
		}

		const payload = {
			details: details,
			records: returnItems.map(({ item, item_name, quantity, amount }) => ({
				item,
				item_desc: item_name,
				quantity,
				amount,
				batch: record.details.batch
			})),
		};

		const res = await api.post(`/sales/returns`, payload);

		if (res) {
			if (res.status === 201 || res.status === 204) {
				toast.success(t("common:toast.success.return"), { autoClose: 3000 });
				onSuccess?.();
				toggle();
			}
		}

		setLoadingMessage(null);
	};

	const returnTotal = returnItems.reduce((sum, ri) => addMoney(sum, lineTotal(ri.quantity, ri.amount)), 0);

	const handleDialogClose = (_event: object, reason: string) => {
		if (reason === "backdropClick" || reason === 'escapeKeyDown') return;
		toggle();
	};

	return (
		<>
			<MoleDialog
				open={isOpen}
				onClose={handleDialogClose}
				maxWidth="lg"
				fullWidth
				themeColor={warningColor}
				paperOptions={{ maxHeight: "95vh" }}
			>
				<MoleDialogHeader
					eyebrow={t("common:return.title")}
					title={record?.details?.reference || bundle.reference}
					icon={<ReplayOutlined />}
					onClose={toggle}
					themeColor={warningColor}
				/>

				<DialogContent sx={{ p: 4 }}>
					{record ? (
						<Box>
							<Grid container spacing={2}>

								{/* Return Date Picker */}
								<Grid size={{ xs: 12, md: 6 }}>
									{/* Read-only Metadata */}
									<Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: "background.default", borderStyle: "dashed" }}>
										<Stack spacing={1}>
											<DetailItem icon={faHashtag} label={t("common:report.invoiceNo")} value={record?.details?.reference} />
											<DetailItem icon={faPerson} label={t("common:report.client")} value={record?.details?.client} />
											<DetailItem icon={faCalendarAlt} label={t("common:report.billingDate")} value={Util.formatDate(record?.details?.date, "full")} />
											<DetailItem icon={faUser} label={t("common:report.invoiceBy")} value={record?.details?.user} />
										</Stack>
									</Paper>
								</Grid>

								<Grid size={{ xs: 12, md: 6 }}>
									{/* Return Details Form */}
									<Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: "background.paper", border: "1px solid", borderColor: "primary.light" }}>
										<Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>
											{t("common:return.details")}
										</Typography>
										<Grid container spacing={0}>

											{/* Return Date Picker */}
											<Grid size={{ xs: 12 }}>
												<LocalizationProvider dateAdapter={AdapterDateFns}>
													<DatePicker
														label={
															<span>
																{t("common:return.date")}:&nbsp;&nbsp;
																<RefreshButton isWorking={false} onClick={() => setReturnDate(new Date())} />
															</span>
														}
														value={returnDate}
														minDate={new Date(record?.details?.date || new Date())}
														maxDate={new Date()}
														onChange={(newDate) => {
															if (!newDate || Util.compareStrings(newDate.toString(), "Invalid Date") || newDate < new Date(record?.details?.date || new Date()) || newDate > new Date()) {
																setErrorInput("dateInput");
																setTimeout(() => setErrorInput(""), 1500);
																return;
															}
															const date = newDate instanceof Date ? newDate : newDate.toDate(); // ✅ safe narrowing
															setReturnDate(date);
														}}
														slotProps={{
															textField: {
																id: "returnDateInput",
																size: "small",
																fullWidth: true,
																variant: "filled",
																error: errorInput === "dateInput",
																helperText:
																	errorInput === "dateInput" ? t("common:validation.invalidDate") : "",
															},
														}}
													/>
												</LocalizationProvider>
											</Grid>

											{/* Description */}
											<Grid size={{ xs: 12 }}>
												<TextField
													id="descriptionInput"
													size="small"
													label={t("common:ui.labels.notes")}
													variant="filled"
													fullWidth
													type="text"
													value={description}
													inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
													helperText={`${description.length}/${MYSQL_LIMITS.TEXT.maxLength}`}
													onChange={(e) => setDescription(e.target.value)}
													placeholder={t("common:return.placeholder")}
												/>
											</Grid>
										</Grid>
									</Paper>
								</Grid>
							</Grid>

							{/* Dual Lists */}
							<Grid container spacing={3}>
								{/* Left List: Invoice Items */}
								<Grid size={{ xs: 12, md: 6 }}>
									<Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
										{t("common:return.invoiceItems")}
									</Typography>
									<TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, maxHeight: 400 }}>
										<Table size="small" stickyHeader>
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontWeight: 700 }}>{t("common:report.description")}</TableCell>
													<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:report.quantity")}</TableCell>
													<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:report.unitPrice")}</TableCell>
													<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:report.total")}</TableCell>
													<TableCell align="center" sx={{ fontWeight: 700 }}></TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{record.records?.map((item: any, idx: number) => {
													const added = isItemInReturnList(item);
													const returnedItem = returnedItems.find((ret: ReturnedRecord) => ret.item === item.item_id);
													const allReturned = roundQty(item.quantity - (returnedItem?.quantity ?? 0)) <= 0;
													return (
														<TableRow key={idx} sx={{ opacity: (allReturned || added) ? 0.4 : 1 }}>
															<TableCell>
																<Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
																<Typography variant="caption" color="text.secondary">{item.item_code || t("common:ui.placeholders.notAvailable")}</Typography>
																<Typography variant="body2" color="text.secondary">{t("common:return.alreadyReturned")}: {returnedItem?.quantity ?? 0}</Typography>
															</TableCell>
															<TableCell align="right">{Util.formatNumber(item.quantity)}</TableCell>
															<TableCell align="right">{Util.formatNumber(item.unit_price || item.amount)}</TableCell>
															<TableCell align="right" sx={{ fontWeight: 700 }}>{Util.formatNumber(item.total)}</TableCell>
															<TableCell align="center">
																<Tooltip title={t("common:return.add")}>
																	<span>
																		<IconButton
																			size="small"
																			color="warning"
																			disabled={allReturned || added}
																			onClick={() => handleAddItem(item)}
																		>
																			<AddCircleOutline fontSize="small" />
																		</IconButton>
																	</span>
																</Tooltip>
															</TableCell>
														</TableRow>
													);
												})}
											</TableBody>
										</Table>
									</TableContainer>
								</Grid>

								{/* Right List: Items to Return */}
								<Grid size={{ xs: 12, md: 6 }}>
									<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
										<Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
											{t("common:return.returnItems")}
										</Typography>
										{returnItems.length > 0 && (
											<Chip
												label={`${t("common:report.total")}: ${Util.formatNumber(returnTotal)}`}
												color="warning"
												size="small"
												sx={{ fontWeight: 700 }}
											/>
										)}
									</Stack>
									<TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, maxHeight: 400 }}>
										<Table size="small" stickyHeader>
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontWeight: 700 }}>{t("common:report.description")}</TableCell>
													<TableCell align="center" sx={{ fontWeight: 700 }}>{t("common:return.returnQty")}</TableCell>
													<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:report.unitPrice")}</TableCell>
													<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:report.total")}</TableCell>
													<TableCell align="center" sx={{ fontWeight: 700 }}></TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{returnItems.length === 0 ? (
													<TableRow>
														<TableCell colSpan={5} align="center">
															<Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 4 }}>
																{t("common:return.noItems")}
															</Typography>
														</TableCell>
													</TableRow>
												) : (
													returnItems.map((item, idx) => (
														<TableRow key={idx}>
															<TableCell>
																<Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
																<Typography variant="caption" color="text.secondary">{item.item_code || t("common:ui.placeholders.notAvailable")}</Typography>
															</TableCell>
															<TableCell align="center">
																<Box sx={{ width: 88, mx: "auto" }}>
																	<QuantityStepperInput
																		compact
																		size="small"
																		fullWidth
																		value={item.quantity}
																		fractional={item.isFractional}
																		min={qtyMin(!!item.isFractional)}
																		max={item.maxQuantity}
																		onChange={(newValue) => handleQuantityChange(item, newValue)}
																	/>
																</Box>
															</TableCell>
															<TableCell align="right">{Util.formatNumber(item.amount)}</TableCell>
															<TableCell align="right" sx={{ fontWeight: 700 }}>
																{Util.formatNumber(lineTotal(item.quantity, item.amount))}
															</TableCell>
															<TableCell align="center">
																<Tooltip title={t("common:return.remove")}>
																	<IconButton
																		size="small"
																		color="error"
																		onClick={() => handleRemoveItem(item)}
																	>
																		<RemoveCircleOutline fontSize="small" />
																	</IconButton>
																</Tooltip>
															</TableCell>
														</TableRow>
													))
												)}
											</TableBody>
										</Table>
									</TableContainer>
								</Grid>
							</Grid>
						</Box>
					) : (
						<Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
							<CircularProgress thickness={5} size={48} sx={{ color: "warning.main" }} />
							<Typography sx={{ mt: 2, color: "text.secondary" }}>{t("common:report.fetchingDetails")}</Typography>
						</Stack>
					)}
				</DialogContent>

				<MoleDialogActions>
					<MoleCancelButton onClick={toggle}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
					<MolePrimaryButton
						color="warning"
						startIcon={<ReplayOutlined />}
						onClick={() => setConfirmOpen(true)}
						disabled={!canValidate}
					>
						{t("common:return.confirm")}
					</MolePrimaryButton>
				</MoleDialogActions>
			</MoleDialog>

			<ConfirmDialog
				onConfirm={handleConfirm}
				isOpen={confirmOpen}
				setIsOpen={setConfirmOpen}
				title={t("common:return.confirmTitle")}
				message={
					<>
						{t("common:return.confirmMessage", {
							count: returnItems.length,
							total: Util.formatCurrency(returnTotal, appSettings?.BIZ_CURRENCY?.value || "USD"),
							reference: bundle?.reference,
						})}
					</>
				}
				confirmBtnColor="warning"
				confirmBtnVariant="contained"
				confirmBtnText={<>{t("common:return.confirm")}</>}
			/>
		</>
	);
};

const DetailItem = ({ icon, label, value }: { icon: any; label: string; value: string }) => (
	<Stack direction="row" spacing={1.5} alignItems="center">
		<FontAwesomeIcon icon={icon} style={{ width: 14, color: "#64748b" }} />
		<Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
			{label}:
		</Typography>
		<Typography variant="body2" fontWeight={600}>
			{value || "—"}
		</Typography>
	</Stack>
);

export default ReturnSaleModal;
